import React, { useRef, useState, useEffect } from "react";
import {
  Pause,
  PlayArrow,
  Stop,
  VolumeOff,
  VolumeUp,
} from "@mui/icons-material";
import Card from "./Card";
import Box from "./Box";
import Paragraph from "./Paragraph";
import Button from "./Button";
import { useTheme } from "styled-components";
import Slider from "./Slider";
import { ThemeType } from "app/types/theme";

interface AudioPlayerProps {
  audioUrl: string;
}

const AudioPlayer: React.FC<AudioPlayerProps> = ({ audioUrl }) => {
  const audioRef = useRef<HTMLAudioElement>(null);
  const [playing, setPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(1);
  const [volumeComparativo, setVolumeComparativo] = useState(1);

  const theme: ThemeType = useTheme() as ThemeType;

  useEffect(() => {
    const audioElement = audioRef.current;

    if (audioElement) {
      const onLoadedMetadata = () => {
        setDuration(audioElement.duration);
      };

      audioElement.addEventListener("loadedmetadata", onLoadedMetadata);

      return () => {
        audioElement.removeEventListener("loadedmetadata", onLoadedMetadata);
      };
    }
  }, []);

  useEffect(() => {
    let interval: NodeJS.Timeout | null = null;

    if (playing) {
      interval = setInterval(() => {
        if (audioRef.current) {
          setCurrentTime(audioRef.current.currentTime);
        }
      }, 100);
    } else if (!playing && interval) {
      clearInterval(interval);
    }

    return () => {
      if (interval) clearInterval(interval);
    };
  }, [playing]);

  const togglePlay = () => {
    const audioElement = audioRef.current;

    if (audioElement) {
      if (playing) {
        audioElement.pause();
      } else {
        audioElement.play();
      }
      setPlaying(!playing);
    }
  };

  const stopAudio = () => {
    const audioElement = audioRef.current;

    if (audioElement) {
      audioElement.pause();
      audioElement.currentTime = 0;
      setPlaying(false);
      setCurrentTime(0);
    }
  };

  const handleVolumeChange = (value: number) => {
    const audioElement = audioRef.current;

    if (audioElement) {
      audioElement.volume = value;
      setVolume(value);
      setVolumeComparativo(value);
    }
  };

  const handleSeek = (value: number) => {
    const audioElement = audioRef.current;

    if (audioElement) {
      audioElement.currentTime = value;
      setCurrentTime(value);
    }
  };

  const formatTime = (time: number) => {
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds < 10 ? "0" : ""}${seconds}`;
  };

  const volumeMuteChange = () => {
    const audioElement = audioRef.current;
    if (audioElement) {
      if (volume !== 0) {
        setVolume(0);
        audioElement.volume = 0;
      } else {
        setVolume(volumeComparativo);
        audioElement.volume = volumeComparativo;
      }
    }
  };

  return (
    <Card style={styles.container}>
      <audio ref={audioRef} src={audioUrl} preload="metadata" />
      <Box style={styles.progressContainer}>
        <Paragraph style={{ width: "30px", textAlign: "center" }}>
          {formatTime(currentTime)}
        </Paragraph>
        <Slider
          min={0}
          max={duration || 0}
          value={currentTime}
          setValue={handleSeek}
        />
        <Paragraph style={{ width: "30px", textAlign: "center" }}>
          {formatTime(duration - currentTime)}
        </Paragraph>
      </Box>

      <Box
        style={{
          justifyContent: "space-between",
          flexDirection: "row",
          padding: "0",
        }}
      >
        <Box style={styles.volumeContainer}>
          <Button
            onClick={volumeMuteChange}
            style={{ backgroundColor: "transparent", color: theme.text }}
            styleHover={{ backgroundColor: "transparent", color: theme.text }}
          >
            {volume === 0 ? <VolumeOff /> : <VolumeUp />}
          </Button>

          <Slider
            min={0}
            max={1}
            step={0.01}
            value={volume}
            setValue={handleVolumeChange}
          />
        </Box>
        <Box style={styles.controlsContainer}>
          <Button
            style={{ backgroundColor: "transparent", color: theme.text }}
            styleHover={{ backgroundColor: "transparent", color: theme.text }}
            onClick={togglePlay}
          >
            {playing ? <Pause /> : <PlayArrow />}
          </Button>
          <Button
            onClick={stopAudio}
            disabled={!playing}
            style={{ backgroundColor: "transparent", color: theme.text }}
            styleHover={{ backgroundColor: "transparent", color: theme.text }}
          >
            <Stop />
          </Button>
        </Box>
      </Box>
    </Card>
  );
};

const styles: Record<string, React.CSSProperties> = {
  container: {
    boxShadow: "none",
    border: "none",
    backgroundColor: "transparent",
    width: "100%",
  },
  progressContainer: {
    display: "flex",
    alignItems: "center",
    width: "100%",
    gap: "0.5rem",
  },
  controlsContainer: {
    display: "flex",
    gap: "1rem",
    alignItems: "center",
  },
  volumeContainer: {
    display: "flex",
    alignItems: "center",
    gap: "0.5rem",
  },
};

export default AudioPlayer;
